import { MaterialReactTable } from 'material-react-table'
import { Box } from '@mui/material'
import { useEnumContext } from '@modules/enums/context/enum'
import { type PropsWithChildren, useState, useMemo, useCallback } from 'react'
import type { TPublisherRequest } from '@modules/types'
import { useList, useGo, useNavigation } from '@refinedev/core'
import { useLocation } from 'react-router'
import { PUBLISHER_EXPAND_PRESETS } from '@modules/api'
import { useAnalytics } from '@publisher/modules/analytics'
import { usePublisherRequestsListSorting } from '../../hooks'
import { TableRequestsState } from '../RequestsTable'
import { createRequestsListColumns } from './columns'
import {
  requestTypeFilterToListFilter,
  type TPublisherRequestListTypeFilter,
} from './RequestTypeColumnHeader'
import {
  reviewTypeFilterToListFilter,
  type TPublisherRequestListReviewTypeFilter,
} from './ReviewTypeColumnHeader'
import {
  statusFilterToListFilter,
  type TPublisherRequestListStatusFilter,
} from './StatusColumnHeader'

export const RequestsList = ({ children }: PropsWithChildren) => {
  const go = useGo()
  const { pathname } = useLocation()
  const { showUrl } = useNavigation()
  const { enums, loading: enumsLoading } = useEnumContext()
  const { analytics, channels } = useAnalytics()
  const [search, setSearch] = useState('')
  const [hideClosed, setHideClosed] = useState(false)
  const [requestTypeFilter, setRequestTypeFilter] = useState<TPublisherRequestListTypeFilter>(null)
  const [reviewTypeFilter, setReviewTypeFilter] =
    useState<TPublisherRequestListReviewTypeFilter>(null)
  const [statusFilter, setStatusFilter] = useState<TPublisherRequestListStatusFilter>(null)
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const resetToFirstPage = useCallback(() => {
    setPagination(p => ({ ...p, pageIndex: 0 }))
  }, [])

  const { sorting, listSorters, handleSortingChange } = usePublisherRequestsListSorting({
    resetToFirstPage,
  })

  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch)
    resetToFirstPage()
  }

  const handleHideClosedChange = (newHideClosed: boolean) => {
    setHideClosed(newHideClosed)
    resetToFirstPage()
  }

  const handleStatusFilterChange = useCallback(
    (filter: TPublisherRequestListStatusFilter) => {
      setStatusFilter(filter)
      resetToFirstPage()
    },
    [resetToFirstPage],
  )

  const handleRequestTypeFilterChange = useCallback(
    (filter: TPublisherRequestListTypeFilter) => {
      setRequestTypeFilter(filter)
      resetToFirstPage()
    },
    [resetToFirstPage],
  )

  const handleReviewTypeFilterChange = useCallback(
    (filter: TPublisherRequestListReviewTypeFilter) => {
      setReviewTypeFilter(filter)
      resetToFirstPage()
    },
    [resetToFirstPage],
  )

  const { data, isLoading, isFetching, isError } = useList<TPublisherRequest>({
    resource: 'publisher-request',
    pagination: {
      mode: 'server',
      current: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    },
    sorters: listSorters,
    filters: [
      ...requestTypeFilterToListFilter(requestTypeFilter),
      ...reviewTypeFilterToListFilter(reviewTypeFilter),
      ...statusFilterToListFilter(statusFilter),
      ...(search
        ? [
            {
              field: 'q',
              operator: 'contains' as const,
              value: search,
            },
          ]
        : []),
      {
        field: 'hide_closed',
        operator: 'eq' as const,
        value: hideClosed,
      },
    ],
    meta: PUBLISHER_EXPAND_PRESETS.CLIENT_REQUEST,
    queryOptions: {
      enabled: true,
    },
  })

  const baseColumns = useMemo(
    () =>
      createRequestsListColumns({
        enums,
        enumsLoading,
        isFetching,
        go,
        pathname,
        showUrl,
        analytics,
        channels,
        requestTypeFilter,
        onRequestTypeFilterChange: handleRequestTypeFilterChange,
        reviewTypeFilter,
        onReviewTypeFilterChange: handleReviewTypeFilterChange,
        statusFilter,
        onStatusFilterChange: handleStatusFilterChange,
      }),
    [
      enums,
      enumsLoading,
      isFetching,
      go,
      pathname,
      showUrl,
      analytics,
      channels,
      requestTypeFilter,
      handleRequestTypeFilterChange,
      reviewTypeFilter,
      handleReviewTypeFilterChange,
      statusFilter,
      handleStatusFilterChange,
    ],
  )

  const requests = data?.data || []

  return (
    <>
      <TableRequestsState
        isLoading={isLoading}
        isError={isError}
        hasData={requests.length > 0}
        search={search}
        onSearchChange={handleSearchChange}
        hideClosed={hideClosed}
        onHideClosedChange={handleHideClosedChange}
        pagination={pagination}
        columnsCount={baseColumns.length}
      >
        <Box sx={{ position: 'relative' }}>
          <MaterialReactTable
            columns={baseColumns}
            data={requests}
            enableColumnFilters={false}
            enableToolbarInternalActions={false}
            enablePagination
            enableBottomToolbar
            paginationDisplayMode="pages"
            enableTopToolbar={false}
            manualPagination
            rowCount={data?.total || 0}
            localization={{
              noRecordsToDisplay: '',
            }}
            onPaginationChange={updater => {
              if (typeof updater === 'function') {
                const newState = updater(pagination)
                setPagination(newState)
              }
            }}
            manualSorting
            enableSorting
            onSortingChange={handleSortingChange}
            state={{
              pagination: {
                pageIndex: pagination.pageIndex,
                pageSize: pagination.pageSize,
              },
              sorting,
            }}
            muiPaginationProps={{
              rowsPerPageOptions: [10, 50, 100],
              showFirstButton: true,
              showLastButton: true,
              SelectProps: {
                MenuProps: {
                  anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                },
              },
            }}
            enableColumnResizing={false}
            layoutMode="semantic"
            defaultColumn={{ size: 100, minSize: 40 }}
            enableStickyHeader
            enableStickyFooter
            muiTablePaperProps={{
              elevation: 0,
            }}
            muiTableBodyCellProps={{
              sx: {
                backgroundColor: 'background.paper',
                color: 'text.primary',
                fontSize: 15,
                px: 2.5,
                py: 2.25,
                borderBottomColor: 'divider',
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: 'background.paper',
                color: 'text.secondary',
                fontSize: 14,
                fontWeight: 500,
                px: 2.5,
                py: 1,
                whiteSpace: 'nowrap',
                verticalAlign: 'middle',
                borderBottomColor: 'divider',
                '.Mui-TableHeadCell-Content-Wrapper': { whiteSpace: 'nowrap' },
              },
            }}
            muiBottomToolbarProps={{
              sx: {
                backgroundColor: 'background.paper',
                boxShadow: 'none',
              },
            }}
            muiTableBodyRowProps={{
              sx: {
                '&:last-of-type td': {
                  borderBottom: 'none',
                },
              },
            }}
            muiTableContainerProps={{
              sx: {
                borderRadius: 0,
                height: 'calc(100vh - 330px)',
                backgroundColor: 'background.paper',
              },
            }}
          />
          {requests.length === 0 && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                color: 'text.secondary',
              }}
            >
              No records to display
            </Box>
          )}
        </Box>
      </TableRequestsState>
      {children}
    </>
  )
}
